<template>
  <Modal
    v-model:visible="isVisible"
    class="carrier-portal-update-expected-arrival-modal"
    :header="
      t('common.updateParam', { param: t('shipments.dates.expectedArrival') })
    "
    size="md"
    @keypress.enter="onSave"
    @keypress.esc="onCancel"
  >
    <div class="flex flex-gap-1">
      <div class="w-6">
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          input-id="expected-arrival"
          class="field"
          :label="t('shipments.dates.expectedArrival')"
          :errors="v$.expectedArrival.$errors"
        >
          <div class="flex flex-gap-1 event-calendar-tz-wrapper">
            <RygenCalendar
              :id="inputId"
              v-model="internalValue.expectedArrival"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :time-zone="internalValue.actualLocation.timeZone"
              class="flex-grow-1"
              date-format="mm/dd/yy"
              show-icon
              show-time
              hour-format="12"
              :step-minute="15"
              :placeholder="t('shipments.dates.expectedArrival')"
            />
            <TimeZoneDropdown
              v-model="internalValue.actualLocation.timeZone"
              :date-time-basis="internalValue.expectedArrival"
              :disabled="!!internalValue.relatedStop?.id"
              :placeholder="t('common.timezones')"
            />
          </div>
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-comment"
          class="field"
          :label="t('common.comments')"
          optional
        >
          <Textarea
            :id="inputId"
            v-model="internalValue.comment"
            v-bind="ariaProps"
            rows="5"
            cols="30"
            :placeholder="t('common.comments')"
            @keypress.enter="$event.stopPropagation()"
            @keypress.shift.enter="onSave"
          />
        </InputWrapper>
      </div>

      <div class="w-6">
        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-related-stop-id"
          class="field"
          :label="t('common.relatedParam', { param: t('shipments.stops') })"
          optional
        >
          <RygenDropdown
            v-bind="ariaProps"
            :id="inputId"
            append-to="body"
            :model-value="internalValue.relatedStop?.id"
            :options="[relatedStop]"
            option-value="id"
            data-key="id"
            disabled
          >
            <template #value>
              <StopLabel :stop="relatedStop" />
            </template>
          </RygenDropdown>
        </InputWrapper>

        <!-- TODO: Use geolocation API to allow the user to autofill address -->
        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-city"
          class="field"
          :label="t('address.city')"
          optional
        >
          <InputText
            :id="inputId"
            v-model="internalValue.actualLocation.city"
            v-bind="ariaProps"
            disabled
            :placeholder="t('address.city')"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-state"
          class="field"
          :label="t('address.state')"
          optional
        >
          <InputRegion
            :id="inputId"
            v-model="internalValue.actualLocation.state"
            v-bind="ariaProps"
            :country="internalValue.actualLocation.country"
            append-to="body"
            disabled
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          input-id="event-postal-code"
          class="field"
          :label="t('address.postalCode')"
          optional
        >
          <InputText
            :id="inputId"
            v-model="internalValue.actualLocation.postalCode"
            v-bind="ariaProps"
            disabled
            :placeholder="t('address.postalCode')"
          />
        </InputWrapper>
      </div>
    </div>

    <RelatedStopDates :related-stop="relatedStop" />

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-save"
        :disabled="disabled"
        :label="t('common.saveParam', { param: t('events.events') })"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import type { Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useUid, useValidations } from '@rygen/rygen-ui/composables';

import {
  ShipmentEventLocation,
  ShipmentEventRelatedStopReference,
  ShipmentStop,
  UpdateExpectedArrivalRequest,
} from '@/model';

import RelatedStopDates from '@/components/carrierPortal/event/RelatedStopDates.vue';
import StopLabel from '@/components/location/StopLabel.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputRegion from '@rygen/rygen-ui/components/form/InputRegion.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import TimeZoneDropdown from '@rygen/rygen-ui/components/form/TimeZoneDropdown.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  relatedStop: {
    type: ShipmentStop,
    default: () => new ShipmentStop(),
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { relatedStop, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'save', updateRequest: UpdateExpectedArrivalRequest): void;
}>();

const { t } = useI18n();
const { uid } = useUid('carrier-portal-update-expected-arrival-modal');

const internalValue: Ref<UpdateExpectedArrivalRequest> = ref(
  new UpdateExpectedArrivalRequest({
    relatedStop: new ShipmentEventRelatedStopReference({
      id: relatedStop.value.id ?? -1,
      sequenceNumber: relatedStop.value.sequenceNumber ?? -1,
    }),
    actualLocation: new ShipmentEventLocation({
      ...relatedStop.value.location,
    }),
    expectedArrival: relatedStop.value.expectedArrival,
  }),
);
const rules = computed(() => ({
  expectedArrival: {
    required: required(t('shipments.dates.expectedArrival')),
  },
}));
const { v$, validate } = useValidations(internalValue, rules, uid);

const onSave = async () => {
  if (await validate()) {
    emit('save', internalValue.value);
    isVisible.value = false;
  }
};

const onCancel = () => {
  emit('cancel');
  isVisible.value = false;
};

watch(
  isVisible,
  (newVal: boolean) => {
    if (newVal) {
      internalValue.value = new UpdateExpectedArrivalRequest({
        relatedStop: new ShipmentEventRelatedStopReference({
          id: relatedStop.value.id ?? -1,
          sequenceNumber: relatedStop.value.sequenceNumber ?? -1,
        }),
        actualLocation: new ShipmentEventLocation({
          ...relatedStop.value.location,
        }),
        expectedArrival: relatedStop.value.expectedArrival,
      });
      v$.value.$reset();
    }
  },
  { immediate: true },
);
</script>
